import { useMemo } from 'react';
import { cn } from '@rocco/utils/cn';
import { Badge } from '@rocco/components/badge';
import { localizeTime } from '~/modules/root';
import { useUser } from '~/modules/user';
import type { PartialConversation } from '../../types';
import { useChat } from '../../hooks/use-chat';
import { getPreviewMessage } from '../../utils';
import { ParticipantAvatar } from '../ParticipantAvatar';

/**
 * A conversation item in the chat inbox list.
 * @see {@link ChatInboxList}
 */
export function ChatInboxListItem({
    conversation,
}: {
    conversation: PartialConversation;
}) {
    const user = useUser();
    const { openChatBox } = useChat();
    const otherParticipant = useMemo(() => {
        return conversation.__typename === 'EnquiryConversation'
            ? conversation.InitialEnquiry.Professional
            : conversation.Participants.find(
                  ({ Identity }) =>
                      Identity.__typename === 'Member' &&
                      Identity.ID !== user.ID
              )?.Identity;
    }, [conversation, user.ID]);
    const messagePreview = useMemo(() => {
        return getPreviewMessage(conversation.LatestMessage, user.ID);
    }, [user.ID, conversation.LatestMessage]);
    const lastMessageTime = useMemo(() => {
        return localizeTime(conversation.LatestMessage.Created).format('D MMM');
    }, [conversation.LatestMessage.Created]);
    const lastMessageTimeA11y = useMemo(() => {
        return localizeTime(conversation.LatestMessage.Created).format(
            'YYYY-MM-DD HH:mm'
        );
    }, [conversation.LatestMessage.Created]);
    const unreadCount = useMemo(() => {
        return conversation.UnreadMessageCount || Number(conversation.Unread);
    }, [conversation.UnreadMessageCount, conversation.Unread]);

    return (
        <button
            className={cn(
                'flex items-center gap-3 rounded-lg p-4 text-left hover:bg-gray-200',
                unreadCount > 0 && 'bg-gray-100'
            )}
            aria-label="Open conversation"
            onClick={() => {
                openChatBox(Number(conversation.ID));
            }}
        >
            <ParticipantAvatar
                className="rounded-none"
                imageClassName="rounded-none"
                fallbackClassName="rounded-none"
                size="square48"
                participant={otherParticipant}
            />
            <div className="min-w-0 flex-1 space-y-1">
                <div className="line-clamp-2 break-words text-base font-medium text-foreground">
                    {otherParticipant?.__typename === 'Professional'
                        ? otherParticipant.Title
                        : otherParticipant?.Name}
                </div>
                <div className="truncate text-sm text-muted-foreground">
                    {messagePreview}
                </div>
            </div>
            <div className="space-y-1 text-right">
                <time
                    className="text-sm text-muted-foreground"
                    dateTime={lastMessageTimeA11y}
                >
                    {lastMessageTime}
                </time>
                <div>
                    <Badge
                        size="centeredCompact"
                        color="red"
                        className={cn(unreadCount <= 0 && 'invisible')}
                    >
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </Badge>
                </div>
            </div>
        </button>
    );
}
